import type { Taster } from '../../types'
import { isTasterComplete } from '../../utils/scoring'

type CorrelationHeatmapProps = {
  tasters: Taster[]
}

/**
 * Calculate Spearman's rank correlation coefficient
 * ρ = 1 - (6 × Σd²) / (n × (n² - 1))
 */
function spearmanCorrelation(scoresA: number[], scoresB: number[]): number {
  const n = scoresA.length
  if (n < 2) return 0

  let sumDSquared = 0
  for (let i = 0; i < n; i++) {
    const d = scoresA[i] - scoresB[i]
    sumDSquared += d * d
  }

  return 1 - (6 * sumDSquared) / (n * (n * n - 1))
}

function getCorrelationColor(correlation: number): string {
  if (correlation >= 0.7) return 'bg-green-600 text-white' // Dark green
  if (correlation >= 0.3) return 'bg-green-200 text-green-900' // Light green
  if (correlation > -0.3) return 'bg-gray-100 text-gray-700' // Neutral
  if (correlation > -0.7) return 'bg-red-200 text-red-900' // Light red
  return 'bg-red-600 text-white' // Dark red
}

export function CorrelationHeatmap({ tasters }: CorrelationHeatmapProps) {
  const completeTasters = tasters
    .filter(isTasterComplete)
    .sort((a, b) => a.name.localeCompare(b.name))

  // Build correlation matrix
  const matrix: number[][] = completeTasters.map((tasterA, i) =>
    completeTasters.map((tasterB, j) => {
      if (i === j) return 1
      return spearmanCorrelation(
        tasterA.scores as number[],
        tasterB.scores as number[]
      )
    })
  )

  return (
    <div className="overflow-x-auto flex justify-center">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="p-2 text-left font-medium"></th>
            {completeTasters.map((taster) => (
              <th
                key={taster._key}
                className="p-2 text-center font-medium min-w-[60px] max-w-[80px] truncate"
                title={taster.name}
              >
                {taster.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {completeTasters.map((tasterRow, rowIndex) => (
            <tr key={tasterRow._key}>
              <td
                className="p-2 font-medium text-right max-w-[100px] truncate"
                title={tasterRow.name}
              >
                {tasterRow.name}
              </td>
              {matrix[rowIndex].map((correlation, colIndex) => (
                <td
                  key={completeTasters[colIndex]._key}
                  className={`p-2 text-center font-mono text-xs ${
                    rowIndex === colIndex ? 'bg-gray-50' : getCorrelationColor(correlation)
                  }`}
                  title={
                    rowIndex === colIndex
                      ? tasterRow.name
                      : `${tasterRow.name} vs ${completeTasters[colIndex].name}: ${correlation.toFixed(2)}`
                  }
                >
                  {rowIndex === colIndex ? '' : correlation.toFixed(2)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
